import { unzip } from "fflate"
import { MAX_TDATA_FILES, REQUIRED_TDATA_FILES } from "../clients/tdataUtils"

export interface TdataParseResult {
  files: File[] | null
  error: string | null
}

const dirOf = (path: string): string => {
  const idx = path.lastIndexOf("/")
  return idx === -1 ? "" : path.slice(0, idx)
}

const baseName = (path: string): string => {
  const idx = path.lastIndexOf("/")
  return idx === -1 ? path : path.slice(idx + 1)
}

async function readFileAsUint8Array(file: File): Promise<Uint8Array> {
  if (typeof file.arrayBuffer === "function") {
    return new Uint8Array(await file.arrayBuffer())
  }
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      resolve(new Uint8Array(reader.result as ArrayBuffer))
    }
    reader.onerror = () => reject(reader.error)
    reader.readAsArrayBuffer(file)
  })
}

/**
 * Распаковка zip с tdata одного аккаунта (1 zip = 1 аккаунт).
 * Ищет первую папку (в порядке следования записей zip) с полным набором
 * обязательных файлов на любом уровне вложенности; из неё берёт все файлы
 * (≤ MAX_TDATA_FILES) с короткими именами — как из корня tdata.
 * Остальное игнорируется: бэк сам берёт нужные файлы.
 */
export async function parseTdataZip(zipFile: File): Promise<TdataParseResult> {
  let entries: Record<string, Uint8Array>
  try {
    const buf = await readFileAsUint8Array(zipFile)
    entries = await new Promise((resolve, reject) =>
      unzip(buf, (err, data) => (err ? reject(err) : resolve(data))),
    )
  } catch {
    return { files: null, error: "Битый или нечитаемый zip-архив" }
  }

  // Группировка записей по папкам с сохранением порядка первого появления.
  const folders = new Map<string, Set<string>>()
  const order: string[] = []
  for (const path of Object.keys(entries)) {
    if (path.endsWith("/")) continue // записи-директории
    const folder = dirOf(path)
    let names = folders.get(folder)
    if (!names) {
      names = new Set()
      folders.set(folder, names)
      order.push(folder)
    }
    names.add(baseName(path))
  }

  for (const folder of order) {
    const names = folders.get(folder)!
    const complete = REQUIRED_TDATA_FILES.every((n) => names.has(n))
    if (!complete) continue
    const files = Object.keys(entries)
      .filter((p) => !p.endsWith("/") && dirOf(p) === folder)
      .slice(0, MAX_TDATA_FILES)
      .map((p) => new File([entries[p]], baseName(p)))
    return { files, error: null }
  }

  return {
    files: null,
    error: `Не найдены обязательные файлы tdata: ${REQUIRED_TDATA_FILES.join(", ")}`,
  }
}
